import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import {
  FloatingArrow,
  arrow,
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";

export interface HeaderPopoverRef {
  toggle: () => void;
  open: () => void;
  close: () => void;
  isOpen: () => void;
}

const HeaderPopover = function HeaderPopover({
  ref,
  ...p
}: PropsWithChildren<{
  buttonContent: ReactNode;
  onClose?: () => void;
}> & {
  ref?: React.RefObject<HeaderPopoverRef>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating<HTMLButtonElement>({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: 24 }),
      shift({ padding: 16 }),
      arrow({ element: arrowRef }),
    ],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useImperativeHandle<HeaderPopoverRef, HeaderPopoverRef>(
    ref,
    () => {
      return {
        toggle: () =>
          refs.reference.current && refs.domReference.current.click(),
        open: () => !isOpen && refs.domReference.current.click(),
        close: () => isOpen && refs.domReference.current.click(),
        isOpen: () => !!isOpen,
      };
    },
    [isOpen],
  );

  useEffect(() => {
    const isClosed = !refs.floating.current;
    if (isClosed && typeof p.onClose === "function") {
      p.onClose();
    }
  }, [refs.floating.current]);

  return (
    <Popover>
      <PopoverButton
        ref={refs.setReference}
        className="flex h-9 w-9 items-center justify-center rounded-full focus:ring-0"
        {...getReferenceProps()}
      >
        {p.buttonContent}
      </PopoverButton>

      <PopoverPanel
        className="z-20 rounded-lg bg-white shadow-[0px_0px_10px_3px_#0004]"
        ref={refs.setFloating}
        style={{ ...floatingStyles }}
        {...getFloatingProps()}
      >
        {p.children}
        <FloatingArrow
          ref={arrowRef}
          context={context}
          width={32}
          height={16}
          stroke="white"
          fill="white"
        />
      </PopoverPanel>
    </Popover>
  );
};

export default HeaderPopover;
